'use client';

import { withBase } from '../lib/brand';
import { stripHtml } from '../lib/richText';
import React, { useState } from 'react';
import { getOptimizedEntityImage } from '../utils/entityImage';
import { toOptimizedImageSrc, DEFAULT_FALLBACK_IMAGE } from '../utils/normalizeImageSrc';

interface Activity {
  id: number;
  name: string;
  short_desc?: string;
  desc?: string;
  description?: string;
  entity_type?: string;
  image_src?: string;
  gallery_images?: { image_src: string }[];
  distance_km: number;
  slug?: string;
  url?: string;
  rating?: string;
  addresses?: { town?: string; state?: string }[];
  features?: { desc?: string; description?: string; icon_src?: string }[];
}

interface NearbyActivitiesProps {
  activities: Activity[];
  campsiteName: string;
}

const TYPE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  tours: { label: 'Tour', color: '#9333ea', bg: '#f3e8ff' },
  poi: { label: 'Attraction', color: '#0369a1', bg: '#e0f2fe' },
};

export default function NearbyActivities({ activities, campsiteName }: NearbyActivitiesProps) {
  const [expanded, setExpanded] = useState(false);

  if (!activities || activities.length === 0) return null;

  const visible = expanded ? activities : activities.slice(0, 4);

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-bold flex items-center gap-2">
          <span className="text-lg">🎯</span> Things to Do Nearby
        </h3>
        <span className="text-xs text-gray-400">within 50km</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Activities and attractions near {campsiteName}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {visible.map((activity) => (
          <ActivityCard key={activity.id} activity={activity} />
        ))}
      </div>

      {activities.length > 4 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 w-full text-center text-sm text-[#1558b0] hover:text-[#1558b0] font-medium py-2 rounded-lg hover:bg-[#e3f2fd] transition-colors"
        >
          {expanded ? 'Show fewer' : `Show all ${activities.length} activities`}
        </button>
      )}
    </div>
  );
}

function ActivityCard({ activity }: { activity: Activity }) {
  const typeInfo = TYPE_LABELS[activity.entity_type || ''] || TYPE_LABELS.poi;
  // stripHtml: RIDB (US) descriptions arrive as HTML fragments.
  const desc = stripHtml(activity.short_desc || activity.desc || activity.description || '');
  const truncatedDesc = desc.length > 120 ? desc.slice(0, 120) + '...' : desc;
  const town = activity.addresses?.[0]?.town || '';
  const state = activity.addresses?.[0]?.state || '';
  const location = [town, state].filter(Boolean).join(', ');
  const url = activity.url || withBase(`/camp-sites/${activity.slug || activity.id}`);

  // Get image - prefer gallery, fall back to entity image
  const rawImg = getOptimizedEntityImage(activity as any, { width: 400, quality: 70 });
  // Check if it's a real image or the default fallback
  const hasRealImage = rawImg && !rawImg.includes('default-campsite-hero');
  const [imgError, setImgError] = useState(false);

  const TypeIcon = () => (
    <div className="w-full h-full flex items-center justify-center"
         style={{ background: activity.entity_type === 'tours' ? 'linear-gradient(135deg, #f3e8ff, #e0e7ff)' : 'linear-gradient(135deg, #e0f2fe, #e3f2fd)' }}>
      <span className="text-3xl">{activity.entity_type === 'tours' ? '🏔️' : '📍'}</span>
    </div>
  );

  return (
    <a
      href={url}
      className="flex bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md hover:border-[#e3f2fd] transition-all group no-underline"
    >
      {/* Image */}
      <div className="w-28 min-h-[100px] flex-shrink-0 relative overflow-hidden bg-gray-100">
        {hasRealImage && !imgError ? (
          <img
            src={rawImg}
            alt={activity.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <TypeIcon />
        )}
        {/* Distance badge */}
        <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full">
          {activity.distance_km}km
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 p-3 min-w-0">
        <div className="flex items-start justify-between gap-1 mb-1">
          <h4 className="font-semibold text-sm text-gray-900 leading-tight line-clamp-2 group-hover:text-[#1558b0] transition-colors">
            {activity.name}
          </h4>
          <span
            className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
            style={{ color: typeInfo.color, backgroundColor: typeInfo.bg }}
          >
            {typeInfo.label}
          </span>
        </div>

        {location && (
          <p className="text-xs text-gray-500 mb-1">{location}</p>
        )}

        {truncatedDesc && (
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
            {truncatedDesc}
          </p>
        )}

        {/* Top features */}
        {activity.features && activity.features.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {activity.features
              .filter(f => f.desc && !['tours', 'poi', 'Activities'].includes(f.desc))
              .slice(0, 3)
              .map((f, i) => (
                <span key={i} className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                  {f.desc}
                </span>
              ))}
          </div>
        )}
      </div>
    </a>
  );
}
